import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';

import { PageHeader } from '@/components/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { CheckboxGroup } from '@/components/ui/CheckboxGroup';
import { FormFieldWide, FormSection } from '@/components/ui/FormSection';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { ROLES } from '@/constants/roles';
import {
  ATTENDANCE_MODE_OPTIONS,
  DATE_FORMAT_OPTIONS,
  GRADING_SYSTEM_OPTIONS,
  WEEK_DAY_OPTIONS,
} from '@/constants/institute';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { ApiRequestError } from '@/lib/apiClient';
import { formatDateTime } from '@/lib/format';
import { toast } from '@/hooks/useToast';
import { institutesApi } from '../api/institutes.api';
import { settingsSchema, type SettingsFormValues } from '../schemas/settings.schema';
import { QueryError } from '@/components/ui/QueryError';
import { applyFieldErrors } from '@/lib/forms';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

/** How wide the server pads an issued serial — `SERIAL_DIGITS` in numbering.service.ts. */
const SERIAL_DIGITS = 4;

/**
 * An institute's operating settings: the shape of its week, how it marks
 * attendance, how it grades, and the series its admission and receipt numbers
 * are drawn from.
 *
 * These are the institute's own configuration rather than its identity, which is
 * why they are edited here and not on the profile screen. Support staff may
 * read them to answer a question; only a SUPER_ADMIN may change them, matching
 * the authorisation on the endpoint itself.
 *
 * The bank, gateway and SMS credentials stored on the same row are not part of
 * this screen — the API never sends them to the browser.
 */
export function InstituteSettingsPage() {
  const { uuid = '' } = useParams();
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.user?.role);
  const canEdit = role === ROLES.SUPER_ADMIN;

  const [formError, setFormError] = useState<string | null>(null);

  const { data: institute } = useApiQuery(() => institutesApi.get(uuid), [uuid]);
  const {
    data: settings,
    isLoading,
    error,
    refetch,
  } = useApiQuery(() => institutesApi.getSettings(uuid), [uuid]);

  const form = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsSchema),
    mode: 'onBlur',
  });
  const {
    register,
    control,
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = form;
  useLiveFormErrors(form);

  // Fill the form once the settings arrive, and again after a save, so the
  // form's "dirty" state matches what is actually stored.
  useEffect(() => {
    if (!settings) return;

    reset({
      dateFormat: settings.dateFormat,
      weekOffDays: settings.weekOffDays,
      workingDaysPerWeek: settings.workingDaysPerWeek,
      schoolStartTime: settings.schoolStartTime,
      schoolEndTime: settings.schoolEndTime,
      periodsPerDay: settings.periodsPerDay,
      periodDurationMinutes: settings.periodDurationMinutes,
      attendanceMode: settings.attendanceMode,
      gradingSystem: settings.gradingSystem,
      passingPercentage: settings.passingPercentage,
      admissionNumberPrefix: settings.admissionNumberPrefix,
      admissionNumberNext: settings.admissionNumberNext,
      receiptPrefix: settings.receiptPrefix,
      receiptNumberNext: settings.receiptNumberNext,
      notifyParentsOnAbsence: settings.notifyParentsOnAbsence,
      notifyParentsOnFeeDue: settings.notifyParentsOnFeeDue,
      notifyParentsOnResult: settings.notifyParentsOnResult,
    });
  }, [settings, reset]);

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      // The resolver validates but hands back the *input* shape. Parsing here
      // applies the coercions — "8" → 8 — so the API gets the types it declares.
      const saved = await institutesApi.updateSettings(uuid, settingsSchema.parse(values));
      // A toast, like every other save in the product.
      toast.success('Settings saved', `Last changed ${formatDateTime(saved.updatedAt)}.`);
      refetch();
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  const backTo = { to: `/platform/institutes/${uuid}`, label: 'Back to institute' };

  if (error) {
    return (
      <>
        <PageHeader title="Institute settings" backTo={backTo} />
        <QueryError title="Could not load these settings" error={error} onRetry={refetch} />
      </>
    );
  }

  if (isLoading || !settings) {
    return (
      <>
        <PageHeader title="Loading settings…" backTo={backTo} />
        <div className="card p-5">
          <SkeletonLines count={6} />
        </div>
      </>
    );
  }

  // Built from this institute's own series so the example is the number the
  // next admission will actually get. The year is left as YYYY: it is the
  // session's start year, which these settings do not hold.
  const nextAdmissionExample = `${settings.admissionNumberPrefix}/YYYY/${String(
    settings.admissionNumberNext,
  ).padStart(SERIAL_DIGITS, '0')}`;

  return (
    <>
      <PageHeader
        eyebrow={institute?.displayName ?? 'Institute'}
        title="Settings"
        description="How this institute's week, marking and numbering are set up. Changing them affects every screen the institute sees."
        backTo={backTo}
      />

      {!canEdit && (
        <Alert tone="info" title="Read only">
          Support accounts can review an institute's settings but not change them. A super
          administrator can make an edit here.
        </Alert>
      )}

      {formError && (
        <Alert tone="critical" title="Could not save">
          {formError}
        </Alert>
      )}


      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6">
        <fieldset disabled={!canEdit || isSubmitting} className="flex flex-col gap-6">
          <FormSection
            title="The institute week"
            description="Drives the timetable, the attendance register and every working-day count."
          >
            <FormFieldWide>
              <Controller
                name="weekOffDays"
                control={control}
                render={({ field }) => (
                  <CheckboxGroup
                    label="Week offs"
                    options={WEEK_DAY_OPTIONS}
                    value={field.value ?? []}
                    onChange={field.onChange}
                    error={errors.weekOffDays?.message}
                    hint="Days the institute is closed every week. Second Saturdays and holidays are handled by the academic calendar, not here."
                    columns={3}
                  />
                )}
              />
            </FormFieldWide>
            <Input
              label="Working days per week"
              type="number"
              inputMode="numeric"
              error={errors.workingDaysPerWeek?.message}
              {...register('workingDaysPerWeek')}
            />
            <Select
              label="Attendance is marked"
              options={ATTENDANCE_MODE_OPTIONS}
              hint="Once a day suits primary classes; every period suits senior institute."
              error={errors.attendanceMode?.message}
              {...register('attendanceMode')}
            />
            <Input
              label="Institute starts"
              type="time"
              error={errors.schoolStartTime?.message}
              {...register('schoolStartTime')}
            />
            <Input
              label="Institute ends"
              type="time"
              error={errors.schoolEndTime?.message}
              {...register('schoolEndTime')}
            />
            <Input
              label="Periods per day"
              type="number"
              inputMode="numeric"
              error={errors.periodsPerDay?.message}
              {...register('periodsPerDay')}
            />
            <Input
              label="Minutes per period"
              type="number"
              inputMode="numeric"
              error={errors.periodDurationMinutes?.message}
              {...register('periodDurationMinutes')}
            />
          </FormSection>

          <FormSection
            title="Marking and results"
            description="How marks are recorded and what counts as a pass on the report card."
          >
            <Select
              label="Grading system"
              className="py-2"
              options={GRADING_SYSTEM_OPTIONS}
              error={errors.gradingSystem?.message}
              {...register('gradingSystem')}
            />
            <Input
              label="Passing percentage"
              type="number"
              inputMode="numeric"
              hint="Applied to every subject unless the exam sets its own."
              error={errors.passingPercentage?.message}
              {...register('passingPercentage')}
            />
          </FormSection>

          <FormSection
            title="Numbering"
            description={`The next admission number will be ${nextAdmissionExample}, YYYY being the year the academic session starts. Receipts take the same year: a new session changes the year in the number, and the serial after it keeps counting rather than starting again at 1.`}
          >
            <Input
              label="Admission number prefix"
              placeholder="ADM"
              error={errors.admissionNumberPrefix?.message}
              {...register('admissionNumberPrefix')}
            />
            <Input
              label="Next admission number"
              type="number"
              inputMode="numeric"
              hint="Set this when moving from an existing register, so numbering carries on rather than starting again."
              error={errors.admissionNumberNext?.message}
              {...register('admissionNumberNext')}
            />
            <Input
              label="Receipt prefix"
              placeholder="RCPT"
              error={errors.receiptPrefix?.message}
              {...register('receiptPrefix')}
            />
            <Input
              label="Next receipt number"
              type="number"
              inputMode="numeric"
              error={errors.receiptNumberNext?.message}
              {...register('receiptNumberNext')}
            />
          </FormSection>

          <FormSection
            title="Parent notifications"
            description="What this institute's parents are told automatically. They still see everything in the parent portal."
            columns={1}
          >
            <Checkbox
              label="Tell parents when their child is marked absent"
              {...register('notifyParentsOnAbsence')}
            />
            <Checkbox
              label="Tell parents when a fee instalment falls due"
              {...register('notifyParentsOnFeeDue')}
            />
            <Checkbox
              label="Tell parents when a result is published"
              {...register('notifyParentsOnResult')}
            />
          </FormSection>

          <FormSection
            title="Regional"
            description="Dates, currency and language follow Indian conventions for every institute on the platform — only the date format is set per institute."
          >
            <Select
              label="Date format"
              options={DATE_FORMAT_OPTIONS}
              error={errors.dateFormat?.message}
              {...register('dateFormat')}
            />
          </FormSection>
        </fieldset>

        {canEdit && (
          <div className="flex items-center justify-between gap-3">
            <Button variant="ghost" onClick={() => navigate(`/platform/institutes/${uuid}`)}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="lg"
              isLoading={isSubmitting}
              disabled={!isDirty}
              leftIcon={<Save className="h-4 w-4" />}
            >
              {isSubmitting ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
        )}
      </form>
    </>
  );
}
